import { jwtDecode } from "jwt-decode";
import pluralize from "pluralize";
import moment from "moment";
import { format, parse } from "date-fns";


export const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        return decodedToken.exp < currentTime;
    } catch (error) {
        console.error("Error decoding token:", error);
        return true;
    }
};

export const toCapitalCase = (str) => str.replace(/\b\w/g, (char) => char.toUpperCase());

export const toCamelCase = (str) =>
    str
        .replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => (index === 0 ? match.toLowerCase() : match.toUpperCase()))
        .replace(/\s+/g, "");

export const toPlural = (str) => pluralize(str);

export const isEmpty = (val) => val === undefined || val === null || val === "";

export const formatStatus = (status) => {
    if (!status) return "";
    return status
        .replace(/[_-]+/g, " ") // turn underscores/dashes into spaces
        .split(" ") // split into words
        .filter(Boolean) // guard against empty chunks
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ");
};

export const stringToColor = (string) => {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
};

export const formatCurrency = (
    value,
    {
        abbreviate = false,
        locale = "en-US",
        currency = "USD",
        showSymbol = true,
        useGrouping = true,
        maxFractionDigits = 2,
    } = {}
) => {
    if (typeof value !== "number") {
        value = parseFloat(value);
        if (isNaN(value)) return "—";
    }

    // Handle abbreviations: 1K, 1M, 1B
    if (abbreviate) {
        const abs = Math.abs(value);
        const suffixes = [
            { limit: 1e9, suffix: "B" },
            { limit: 1e6, suffix: "M" },
            { limit: 1e3, suffix: "K" },
        ];

        for (let { limit, suffix } of suffixes) {
            if (abs >= limit) {
                const shortened = (value / limit).toFixed(maxFractionDigits);
                return `${showSymbol ? getCurrencySymbol(currency, locale) : ""}${shortened}${suffix}`;
            }
        }
    }

    // Fallback to full format
    return new Intl.NumberFormat(locale, {
        style: showSymbol ? "currency" : "decimal",
        currency,
        useGrouping,
        maximumFractionDigits: maxFractionDigits,
    }).format(value);
};

// Utility to fetch symbol only
export const getCurrencySymbol = (currency, locale = "en-US") => {
    return (
        (0)
            .toLocaleString(locale, {
                style: "currency",
                currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            })
            .replace(/\d/g, "")
            .trim() || ""
    );
};

export const stringAvatar = (name, width = 25, height = 25, fontSize = 13) => {
    const trimmedName = name?.trim() || "";
    const parts = trimmedName.split(" ");
    const initials = `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`;
    return {
        sx: {
            bgcolor: "#aaa",
            width,
            height,
            fontSize,
        },
        children: initials.toUpperCase(),
    };
};

export const getShortName = (name) => {
    let initials = "J";
    const trimmedName = name?.trim();
    const nameParts = trimmedName.split(" ");
    initials = `${nameParts[0] ?? ""} ${nameParts[1]?.[0] ?? ""}`;

    return initials;
};

export const convertSelectionToArray = (selectionModel) => {
    if (selectionModel && selectionModel.ids instanceof Set) {
        return [...selectionModel.ids];
    }
    return Array.isArray(selectionModel) ? selectionModel : [];
};

export const buildSelectionSet = (ids) => {
    const idsSet = new Set(Array.isArray(ids) ? ids : []);
    return {
        type: "include",
        ids: idsSet,
    };
};

// Date formatting helpers (MM-DD-YYYY and MM-DD-YYYY HH:MM a)
const pad2 = (n) => String(n).padStart(2, '0');

const toDate = (input) => {
    if (!input) return null;
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
};

export const formatDate = (input) => {
    const d = toDate(input);
    if (!d) return "";
    return `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()}`;
};

export const formatDateTime = (input) => {
    const d = toDate(input);
    if (!d) return "";

    let hours = d.getHours();
    const minutes = pad2(d.getMinutes());
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // convert 0 → 12
    return `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()} ${pad2(hours)}:${minutes} ${ampm}`;
};
